import { FC, useEffect, useMemo, useRef } from 'react';

import { ShareChannelIcon } from '@service/giftStore/components/ShareChannelIcon';
import {
  GiftCouponSummary,
  ShareChannel,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { GiftCsMemo } from '@shared/api/giftCsMemo/giftCsMemo.interface';

import { CouponRow } from './CouponRow';
import { GiftItemEmbed } from './types';

interface CouponTableProps {
  sortedCoupons: GiftCouponSummary[];
  giftItemEmbedMap: Map<string, GiftItemEmbed>;
  canCancel: boolean;
  hasCancellableCoupons: boolean;
  disposeMode: boolean;
  selectedIds: Set<string>;
  disposeSelectedIds: Set<string>;
  isPromoting: boolean;
  /** 다른 쿠폰과 같은 카톡방으로 공유된 쿠폰 ID 집합 */
  crossCouponOverlapIds?: Set<string>;
  /** 구매일 / 만료·교환일 컬럼 숨김 */
  hideDates?: boolean;
  toggleSelect: (id: string) => void;
  toggleDisposeSelect: (id: string) => void;
  /** 재공유이력 칩 클릭 → 재공유 이력 모달 오픈 */
  onOpenShareHistory: (coupon: GiftCouponSummary) => void;
  handlePromoteToReissued: (couponId: string) => Promise<void>;
  onIssueRefund?: (couponId: string) => void;
  onIssueRenewal?: (couponId: string) => void;
  onCancelIssue?: (couponId: string) => void;
  onOpenKakaoShareCallbacks?: (coupon: GiftCouponSummary) => void;
  isIssuing?: boolean;
  /** 공유상태 뱃지 클릭 → 미전달(NotShared)로 되돌리기 */
  onResetShareStatus?: (couponId: string) => Promise<void>;
  isResettingShare?: boolean;
  /** 제공 시 "수신번호"(확인번호) 셀 인라인 편집 활성화 — 저장 후 새로고침 없음 */
  onUpdateAdditionalPhone?: (couponId: string, newPhone: string) => Promise<boolean>;
  /** 전달 시 "중복허용"(추가교환) 컬럼 노출 + 클릭 시 모달 오픈 */
  onOpenExtraRedeem?: (coupon: GiftCouponSummary) => void;
  // ─── 스폰(추가교환) 쿠폰 추가결제 대기건 처리 ───
  onSpawnPay?: (coupon: GiftCouponSummary) => void;
  onSpawnFulfill?: (coupon: GiftCouponSummary) => void;
  onSpawnCancel?: (couponId: string) => void | Promise<void>;
  cancelingSpawnId?: string | null;
  // ─── CS 응대 메모 (전달 시 "CS메모" 컬럼 노출) ───
  /** 쿠폰 _id → 그 쿠폰 단위 메모 (주문 축 1회 조회분에서 가른 것) */
  csMemoByCoupon?: Map<string, GiftCsMemo[]>;
  csMemoLoading?: boolean;
  csMemoUnavailable?: boolean;
  /** 메모장 부제목 (주문자 등) */
  csMemoSubtitle?: string;
  /** 강조·스크롤할 쿠폰 (주문검색 쿠폰 찾기에서 짚어 들어온 건) */
  highlightCouponId?: string;
}

export const CouponTable: FC<CouponTableProps> = ({
  sortedCoupons,
  giftItemEmbedMap,
  canCancel,
  hasCancellableCoupons,
  disposeMode,
  selectedIds,
  disposeSelectedIds,
  isPromoting,
  crossCouponOverlapIds,
  hideDates,
  toggleSelect,
  toggleDisposeSelect,
  onOpenShareHistory,
  handlePromoteToReissued,
  onIssueRefund,
  onIssueRenewal,
  onCancelIssue,
  onOpenKakaoShareCallbacks,
  isIssuing,
  onResetShareStatus,
  isResettingShare,
  onUpdateAdditionalPhone,
  onOpenExtraRedeem,
  onSpawnPay,
  onSpawnFulfill,
  onSpawnCancel,
  cancelingSpawnId,
  csMemoByCoupon,
  csMemoLoading,
  csMemoUnavailable,
  csMemoSubtitle,
  highlightCouponId,
}) => {
  // spawnedFrom(_id) → 원본 쿠폰 해석용. 스폰 쿠폰 행에서 "중복원본"을 표시한다.
  const couponById = useMemo(() => {
    const map = new Map<string, GiftCouponSummary>();
    for (const c of sortedCoupons) map.set(c._id, c);
    return map;
  }, [sortedCoupons]);

  // 강조 대상이 있으면 열릴 때 한 번 그 행까지 스크롤한다 (쿠폰이 수십 장이면 화면 밖)
  const scrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightCouponId || scrolledRef.current === highlightCouponId) return;
    if (!sortedCoupons.some((c) => c._id === highlightCouponId)) return;
    scrolledRef.current = highlightCouponId;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`gift-coupon-row-${highlightCouponId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [highlightCouponId, sortedCoupons]);

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-md border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-left text-gray-500">
            {canCancel && hasCancellableCoupons && !disposeMode && (
              <th className="whitespace-nowrap px-3 py-2 font-medium">선택</th>
            )}
            {disposeMode && (
              <th className="whitespace-nowrap px-3 py-2 font-medium">폐기</th>
            )}
            <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">구분</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">상품</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">가격</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">교환상태</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
            <th
              className="whitespace-nowrap px-3 py-2 font-medium"
              title="카카오톡 공유 웹훅으로 집계된 채팅방 수 (클릭 시 상세 이력)"
            >
              카톡공유
            </th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유채널</th>
            {onOpenExtraRedeem && (
              <th
                className="whitespace-nowrap px-3 py-2 font-medium"
                title="중복 공유된 쿠폰에 추가 교환을 열어 새 수령자가 신청하게 합니다"
              >
                중복허용
              </th>
            )}
            {!hideDates && (
              <>
                <th className="whitespace-nowrap px-3 py-2 font-medium">구매일</th>
                <th className="whitespace-nowrap px-3 py-2 font-medium">만료/교환일</th>
              </>
            )}
            <th className="whitespace-nowrap px-3 py-2 font-medium">공유일</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">열람일</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">신청자</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">신청일시</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">메모</th>
            {!!csMemoByCoupon && (
              <th
                className="whitespace-nowrap px-3 py-2 font-medium"
                title="CS 응대 메모 (클릭 시 메모장)"
              >
                CS메모
              </th>
            )}
            <th
              className="whitespace-nowrap px-3 py-2 font-medium"
              title="카카오톡 공유 웹훅 기준 실제 전달 시각"
            >
              <span className="inline-flex items-center gap-1">
                <ShareChannelIcon channel={ShareChannel.KakaoTalk} className="h-3 w-3" />
                <span>카톡전송일시</span>
              </span>
            </th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">확인일시</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">수신번호</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">재공유이력</th>
            <th className="whitespace-nowrap px-3 py-2 font-medium">환불 · 연장</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sortedCoupons.map((c) => {
            const origin = c.spawnedFrom ? couponById.get(c.spawnedFrom) : undefined;
            return (
              <CouponRow
                key={c._id}
                coupon={c}
                embed={giftItemEmbedMap.get(c.giftItem)}
                canCancel={canCancel}
                hasCancellableCoupons={hasCancellableCoupons}
                disposeMode={disposeMode}
                isSelected={selectedIds.has(c._id)}
                isDisposeSelected={disposeSelectedIds.has(c._id)}
                isPromoting={isPromoting}
                isCrossCouponOverlap={crossCouponOverlapIds?.has(c._id) ?? false}
                hideDates={hideDates}
                toggleSelect={toggleSelect}
                toggleDisposeSelect={toggleDisposeSelect}
                onOpenShareHistory={onOpenShareHistory}
                handlePromoteToReissued={handlePromoteToReissued}
                onIssueRefund={onIssueRefund}
                onIssueRenewal={onIssueRenewal}
                onCancelIssue={onCancelIssue}
                onOpenKakaoShareCallbacks={onOpenKakaoShareCallbacks}
                isIssuing={isIssuing}
                onResetShareStatus={onResetShareStatus}
                isResettingShare={isResettingShare}
                onUpdateAdditionalPhone={onUpdateAdditionalPhone}
                onOpenExtraRedeem={onOpenExtraRedeem}
                onSpawnPay={onSpawnPay}
                onSpawnFulfill={onSpawnFulfill}
                onSpawnCancel={onSpawnCancel}
                cancelingSpawnId={cancelingSpawnId}
                spawnOrigin={origin ? { code: origin.code, seq: origin.seq } : null}
                showCsMemo={!!csMemoByCoupon}
                csMemos={csMemoByCoupon?.get(c._id) ?? []}
                csMemoLoading={csMemoLoading}
                csMemoUnavailable={csMemoUnavailable}
                csMemoSubtitle={csMemoSubtitle}
                isHighlighted={!!highlightCouponId && c._id === highlightCouponId}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
